import React, { PureComponent } from 'react';
import { Box, Dialog, DialogActions, DialogContent, DialogTitle } from '@mui/material';
import { Button } from 'components/Button';
import { array, func, object, bool } from 'prop-types';
import DefinableTable from 'components/DefinableTable';
import { ACTIONS, targetsTableDef } from 'components/Notifications/TargetsTableDef';
import EditIcon from '@mui/icons-material/Edit';
import MultiSelectDialog from 'components/MultiSelectDialog';
import { SimpleText } from 'util/IntlMessages';
import { FormGroup, FormLabel, IconButton, TextField } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { Typography } from '@mui/material';
import { injectIntl } from 'react-intl';
import { InputAdornment } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import debounce from 'lodash/debounce';
import { INTENTION_TYPE } from 'components/MultiSelectDialog/MultiSelectDialog';
import { connect } from 'react-redux';
import { clearUserById, getUserById } from 'actions';
import { Tooltip } from '@mui/material';
import Papa from 'papaparse';

class Targets extends PureComponent {
  static propTypes = {
    clearUserById: func,
    getUserById: func,
    intl: object,
    targets: array,
    // eslint-disable-next-line react/no-unused-prop-types
    exclusions: array,
    onSubmitExclusions: func.isRequired,
    onClose: func.isRequired,
    userDetails: object,
    loading: bool,
  };

  static getDerivedStateFromProps(nextProps, prevState) {
    const { initialTargets } = prevState;
    const { targets, exclusions } = nextProps;
    if (initialTargets !== targets) {
      return {
        filteredTargets: targets,
        exclusions,
        initialTargets: targets,
        selectedRow: null,
        searchText: '',
      };
    }
    return null;
  }

  state = {
    exclusions: [],
    selectedRow: null,
    searchText: '',
    filteredTargets: this.props.targets,
    exclusionsDialogOpen: false,
    exclusionsSearchText: '',
    exclusionsFilteredTargets: this.props.targets,
  };

  componentWillUnmount() {
    this.props.clearUserById();
  }

  openExclusionsDialog = () => {
    const { exclusions } = this.state;
    const { targets } = this.props;
    const exclusionsFilteredTargets = targets;
    const exclusionsDialogSelections = exclusions.map(exid => targets.find(t => t.id === exid));
    this.setState({
      exclusionsDialogOpen: true,
      exclusionsFilteredTargets,
      exclusionsDialogSelections,
    });
  };

  closeExclusionsDialog = () => {
    this.setState({ exclusionsDialogOpen: false });
  };

  toggleDetailPanel = () => {
    const { detailPanelCollapsed } = this.state;
    this.setState({ detailPanelCollapsed: !detailPanelCollapsed });
  };

  handleSubmit = () => {
    const { onSubmitExclusions } = this.props;
    const { exclusions } = this.state;
    onSubmitExclusions(exclusions);
  };

  handleActionClick = ({ row, action, rowIds }) => {
    const { selectedRow, exclusions } = this.state;

    switch (action) {
      case ACTIONS.ROW_SELECT: {
        let newExclusions = exclusions;

        if (selectedRow && selectedRow.id === row.id) {
          newExclusions = exclusions.includes(row.id)
            ? exclusions.filter(exid => exid !== row.id)
            : [...exclusions, row.id];
        } else {
          this.props.getUserById(row.id);
        }
        this.setState({
          selectedRow: row,
          detailPanelCollapsed: false,
          exclusions: newExclusions,
        });
        break;
      }

      case ACTIONS.ROW_SELECT_MULTIPLE:
        this.setState({ exclusions: rowIds });
        break;

      default:
        break;
    }
  };

  handleSearchChange = event => {
    const searchText = event.target.value.toLowerCase();
    if (!searchText.length) {
      this.setState({
        searchText,
        filteredTargets: this.props.targets,
      });
      return;
    }
    this.setState({ searchText }, this.filterTargets);
  };

  filterTargets = debounce(() => {
    const { targets } = this.props;
    const { searchText } = this.state;
    this.setState({
      filteredTargets: targets.filter(target => target.fullName.toLowerCase().includes(searchText)),
    });
  }, 300);

  handleExclusionsDialogChange = selectedOptions => {
    this.setState({ exclusionsDialogSelections: selectedOptions });
  };

  searchExclusionsDialog = text => {
    const exclusionsSearchText = text.toLowerCase();
    if (!exclusionsSearchText.length) {
      this.setState({
        exclusionsSearchText,
        exclusionsFilteredTargets: this.props.targets,
      });
      return;
    }
    this.setState({ exclusionsSearchText }, this.exclusionsFilterTargets);
  };

  exclusionsFilterTargets = debounce(() => {
    const { targets } = this.props;
    const { exclusionsSearchText } = this.state;
    this.setState({
      exclusionsFilteredTargets: targets.filter(target =>
        target.fullName.toLowerCase().includes(exclusionsSearchText),
      ),
    });
  }, 300);

  handleExclusionsDialogSubmit = () => {
    const { exclusionsDialogSelections } = this.state;
    this.setState({ exclusions: exclusionsDialogSelections.map(o => o.id) });
    this.closeExclusionsDialog();
  };

  handleExport = () => {
    const { targets } = this.props;
    const { exclusions } = this.state;

    // Filter targets by excluding those with IDs in exclusions
    const filteredTargets = targets.filter(target => !exclusions.includes(target.id));

    // Map to required format
    const csvData = filteredTargets.map(target => ({
      forename: target.forename,
      surname: target.surname,
      group: target.receiverType.label,
      mobilePhone: `${target.mobilePhone}`,
      email: target.email,
    }));

    // Convert to CSV using PapaParse
    const csv = Papa.unparse(csvData, { header: true });

    // Create a Blob from the CSV
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });

    // Create a link to download it
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.href = url;
    link.setAttribute('download', 'recipients_export.csv');
    document.body.appendChild(link);
    link.click();

    // Cleanup
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  renderExclusionsDialog = () => {
    const { exclusionsDialogSelections, exclusionsFilteredTargets } = this.state;
    return (
      <MultiSelectDialog
        intention={INTENTION_TYPE.MODIFY}
        selectedOptions={exclusionsDialogSelections}
        options={exclusionsFilteredTargets}
        onClose={this.closeExclusionsDialog}
        onChange={this.handleExclusionsDialogChange}
        onSearch={this.searchExclusionsDialog}
        onSubmit={this.handleExclusionsDialogSubmit}
        optionRenderer={person => (
          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            <span>{person.fullName}</span>
            <span style={{ color: '#999999' }}>{person.receiverType.label}</span>
          </Box>
        )}
        titleLabelId="notifications.targets.exclusions-manager.title"
        noResultsLabelId="notifications.targets.exclusions-manager.no-results"
        searchingLabelId="notifications.targets.exclusions-manager.searching"
        placeholderLabelId="notifications.targets.exclusions-manager.search-placeholder"
        noSelectionLabelId="notifications.targets.exclusions-manager.no-selection"
        selectedHeadingLabelId="notifications.targets.exclusions-manager.selected-heading"
        removeActionTooltipLabelId="notifications.targets.exclusions-manager.remove-tooltip"
        submitActionLabelId="notifications.targets.exclusions-manager.submit"
      />
    );
  };

  render() {
    const { onClose, intl, userDetails, loading } = this.props;
    const {
      exclusions,
      detailPanelCollapsed,
      exclusionsDialogOpen,
      selectedRow,
      filteredTargets,
      searchText,
    } = this.state;
    return (
      <Dialog
        open
        onClose={onClose}
        className="notice-template-dialog"
        aria-labelledby="form-dialog-title"
        maxWidth={false}
        PaperProps={{ style: { height: '75vh' } }}
      >
        <DialogTitle id="form-dialog-title" className="dialog-title" style={{ minWidth: '70vw' }}>
          <SimpleText id="notifications.targets.dialog.title" />
          <IconButton color="inherit" onClick={onClose} aria-label="Close">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column' }}>
          <Box sx={{ mx: 2, my: 1, width: '25%' }}>
            <TextField
              sx={{ px: 0 }}
              fullWidth
              placeholder={intl.formatMessage({
                id: 'notifications.targets.search.placeholder',
              })}
              value={searchText}
              onChange={this.handleSearchChange}
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                },
              }}
            />
          </Box>
          <Box sx={{ flexGrow: 1 }} style={{ height: 0 }}>
            <DefinableTable
              loading={loading}
              multiSelect
              invertMultiSelect
              selectedRowIds={exclusions}
              selectedRowId={selectedRow && selectedRow.id}
              tableDef={targetsTableDef}
              data={filteredTargets}
              detailPanelCollapsed={detailPanelCollapsed}
              onDetailPanelToggle={this.toggleDetailPanel}
              userRoles={[]}
              onActionClick={this.handleActionClick}
              footerContent={
                <Box
                  className="pointer-events-none"
                  sx={{ display: 'flex', m: 1, mb: 2, justifyContent: 'flex-end', position: 'sticky' }}
                  style={{
                    paddingBottom: 4,
                    bottom: 0,
                  }}
                >
                  <Tooltip
                    title={intl.formatMessage({
                      id: 'notifications.targets.exclusions-manager.tooltip',
                    })}
                  >
                    <div style={{ background: 'white', borderRadius: '8px' }}>
                      <Button
                        labelId="notifications.targets.exclusions-manager.title"
                        variant="outlined"
                        color="primary"
                        iconLeft={<EditIcon sx={{ mr: 0.5 }} />}
                        className="pointer-events-auto"
                        onClick={this.openExclusionsDialog}
                      />
                    </div>
                  </Tooltip>
                </Box>
              }
            >
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'flex-start',
                  height: '100%',
                  flexDirection: 'column',
                  px: 1,
                }}
              >
                {selectedRow && (
                  <Box
                    style={{ minHeight: '100%' }}
                    sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}
                  >
                    <div>
                      <FormGroup sx={{ pb: 3 }}>
                        <Typography variant="h5" component="div">
                          {selectedRow.fullName}
                        </Typography>
                        <Typography variant="body2" component="div">
                          {selectedRow.mobilePhone}
                        </Typography>
                        <Typography variant="body2" component="div">
                          {selectedRow.email}
                        </Typography>
                      </FormGroup>
                      {userDetails &&
                        userDetails.id === selectedRow.id &&
                        userDetails.details.pupils && (
                          <FormGroup sx={{ pb: 3 }}>
                            <FormLabel component="legend">
                              {intl.formatMessage({
                                id: 'notifications.notice-details.legend.children',
                              })}
                            </FormLabel>
                            {userDetails.details.pupils.map((pupil, index) => (
                              <Typography key={index} variant="body2" component="div">
                                {pupil.fullName}
                              </Typography>
                            ))}
                          </FormGroup>
                        )}
                    </div>
                  </Box>
                )}
                {!selectedRow && (
                  <Box
                    style={{ minHeight: '100%' }}
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'center',
                      maxHeight: '100%',
                    }}
                  >
                    <div>
                      <Typography variant="body1" component="div">
                        {intl.formatMessage({ id: 'notifications.targets.no-selection' })}
                      </Typography>
                    </div>
                  </Box>
                )}
              </Box>
            </DefinableTable>
          </Box>
          {exclusionsDialogOpen && this.renderExclusionsDialog()}
        </DialogContent>
        <DialogActions>
          <Button
            sx={{ mr: 3 }}
            labelId="notifications.notice-details.button.export-targets"
            onClick={this.handleExport}
            color="primary"
          />
          <Button labelId="button.save" onClick={this.handleSubmit} color="primary" />
          <Button labelId="button.cancel" onClick={onClose} color="primary" />
        </DialogActions>
      </Dialog>
    );
  }
}

export default connect(
  ({ users }) => ({
    userDetails: users.userById.data,
  }),
  {
    clearUserById,
    getUserById,
  },
)(injectIntl(Targets));
